import { ChangeDetectionStrategy, Component, forwardRef, input } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { LucideCalendarDays } from '@lucide/angular';
import { FieldBase } from '../shared/field-base';
import { FieldErrorComponent } from '../shared/field-error.component';

/**
 * Wraps the native date input — familiar OS pickers, built-in accessibility.
 * Value format: 'yyyy-MM-dd'.
 */
@Component({
  selector: 'ds-date-picker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet, FieldErrorComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
  ],
  template: `
    <div class="ds-field">
      <label class="ds-field__label" [for]="fieldId">
        {{ label() }}
        @if (required()) {
          <span class="ds-field__required" aria-hidden="true">*</span>
        }
      </label>
      @if (helperText()) {
        <p class="ds-field__helper" [id]="fieldId + '-helper'">{{ helperText() }}</p>
      }
      <div class="date">
        <input
          class="ds-input date__control"
          type="date"
          [id]="fieldId"
          [value]="value() ?? ''"
          [disabled]="disabled()"
          [required]="required()"
          [attr.min]="min() ?? null"
          [attr.max]="max() ?? null"
          [attr.aria-invalid]="errorText() ? 'true' : null"
          [attr.aria-describedby]="describedBy()"
          (change)="handleChange($event)"
          (blur)="onTouched()"
        />
        <span class="date__icon" aria-hidden="true">
          <ng-container
            [ngComponentOutlet]="calendarIcon"
            [ngComponentOutletInputs]="{ size: 24, strokeWidth: 2 }"
          />
        </span>
      </div>
      @if (errorText()) {
        <ds-field-error [errorId]="fieldId + '-error'">{{ errorText() }}</ds-field-error>
      }
    </div>
  `,
  styles: `
    .date {
      position: relative;
    }

    .date__control {
      padding-right: var(--space-7);

      &::-webkit-calendar-picker-indicator {
        /* native indicator stays clickable but invisible over our icon */
        position: absolute;
        right: var(--space-3);
        width: 32px;
        height: 32px;
        opacity: 0;
        cursor: pointer;
      }
    }

    .date__icon {
      position: absolute;
      right: var(--space-4);
      top: 50%;
      transform: translateY(-50%);
      display: inline-flex;
      pointer-events: none;
      color: var(--color-text-secondary);
    }
  `,
})
export class DatePickerComponent extends FieldBase<string> {
  /** 'yyyy-MM-dd' */
  min = input<string | undefined>(undefined);
  /** 'yyyy-MM-dd' */
  max = input<string | undefined>(undefined);

  protected readonly calendarIcon = LucideCalendarDays;

  protected handleChange(event: Event): void {
    const next = (event.target as HTMLInputElement).value;
    this.value.set(next);
    this.onChange(next);
  }
}
